<template>
  <div>
    <div
      class="modal-button cursor-pointer"
      :class="buttonClass"
      @click.prevent="toggleModal"
    >
      <ShareIcon
        class="icon"
        :class="iconClass"
      />
      <p class="d-none d-lg-inline-block m-0 share-text">
        {{ shareButtonText }}
      </p>
    </div>
    <Modal
      v-if="showModal"
      is-visible
      :title="title"
      :wrapper-class="modalClass"
      @onClose="toggleModal"
    >
      <template #body>
        <div class="container-fluid py-3">
          <!-- Socials -->
          <div class="row mb-3">
            <div class="col-12 p-0 d-flex">
              <div
                v-for="(item, index) in socialMediaData"
                :key="item.name"
                :class="index !== 0 && 'ml-3'"
                class="icon-container rounded-circle"
                @click="openSocialWindow(item.name)"
              >
                <font-awesome-icon
                  :icon="item.icon"
                  class="fa-2x"
                  :style="{color: item.iconColor}"
                />
              </div>
            </div>
          </div>

          <!-- Shareable link -->
          <div class="row">
            <div class="col-12 p-0">
              <label class="share-modal-label" for="shareable-link">{{ labelText }}</label>
              <div class="input-group input-group-copy mb-3">
                <input
                  id="shareable-link"
                  type="text"
                  class="form-control"
                  aria-label="Share link"
                  readonly
                  :value="shareableTextWithParams"
                >
                <div
                  class="input-group-addon input-group-copy-btn"
                  @click.prevent="copyToClipboard"
                >
                  <CopyIcon class="icon" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ShareIcon from '~/static/share.svg?inline';
import CopyIcon from '~/static/copy.svg?inline';
import {
  FACEBOOK_SHARE_URL,
  LINKEDIN_SHARE_URL,
  TWITTER_SHARE_URL,
} from '../../constants/global';
import Modal from './Modal';

export default {
  components: {
    ShareIcon,
    CopyIcon,
    Modal,
    FontAwesomeIcon,
  },

  props: {
    labelText: {
      type: String,
      default: '',
    },
    shareButtonText: {
      type: String,
      default: 'Share',
    },
    shareableText: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subheader: {
      type: String,
      default: '',
    },
    buttonClass: {
      type: String,
      default: '',
    },
    iconClass: {
      type: String,
      default: '',
    },
    wrapperClass: {
      type: String,
      default: '',
    },
    params: {
      type: Object,
      default: () => ({
        text: '',
        email: '',
        facebook: '',
        linkedIn: '',
        twitter: '',
      }),
    },
  },

  data() {
    return {
      showModal: false,
      modalClass: this.wrapperClass ? 'share-modal'.concat(' ', this.wrapperClass) : 'share-modal',
      socialMediaData: [
        {
          name: 'email',
          icon: ['fas', 'envelope'],
          iconColor: '#2F4B4C',
        },
        {
          name: 'facebook',
          icon: ['fab', 'facebook'],
          iconColor: '#4267B2',
        },
        {
          name: 'linkedIn',
          icon: ['fab', 'linkedin'],
          iconColor: '#0072B1',
        },
        {
          name: 'twitter',
          icon: ['fab', 'twitter'],
          iconColor: '#1DA1F2',
        },
      ],
    };
  },

  computed: {
    shareableTextWithParams() {
      return this.shareableText + (this.params.text || '');
    },
    getTwitterText() {
      let value = `Don’t miss out on ${this.title}`;
      if (this.subheader) {
        value += ` - ${this.subheader}`;
      }
      return encodeURIComponent(`${value}.\u000aThis deal is only on AppSumo for a limited time!`);
    },
  },

  methods: {
    toggleModal() {
      this.showModal = !this.showModal;
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.shareableTextWithParams);
        this.$toastr.s('Copied!');
      } catch (e) {
        // navigator doesn't exist, copy hidden elem
        // This is just for local envs, as navigator only works on https/localhost
        this.documentCopyToClipboard();
      }
    },

    documentCopyToClipboard() {
      const el = document.createElement('textarea');

      try {
        el.value = this.shareableTextWithParams;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';

        document.body.appendChild(el);

        const selected = document.getSelection().rangeCount > 0
          ? document.getSelection().getRangeAt(0) : false;

        el.select();
        document.execCommand('copy');

        this.$toastr.s('Copied!');
        if (selected) {
          document.getSelection().removeAllRanges();
          document.getSelection().addRange(selected);
        }
      } catch (e) {
        // failed to copy the created element
      } finally {
        document.body.removeChild(el);
      }
    },

    getSocialNetworkURL(socialNetwork) {
      // Additional social network parameters (i.e: utm parameters) to append
      // to the shareableText value.
      const {
        email = '',
        facebook = '',
        twitter = '',
        linkedIn = '',
      } = this.params;
      const params = {
        email: [
          `mailto:?body=${encodeURIComponent(this.shareableText + email)}`,
          `subject=${encodeURIComponent(`Share ${this.title}`)}`,
        ],
        facebook: [
          `u=${this.shareableText}`,
          `quote=${encodeURIComponent(this.shareableText + facebook)}`,
        ],
        twitter: [
          `url=${encodeURIComponent(this.shareableText + twitter)}`,
          `text=${this.getTwitterText}`,
        ],
        linkedIn: [
          `url=${encodeURIComponent(this.shareableText + linkedIn)}`,
        ],
      };
      const urls = {
        email: `${params[socialNetwork].join('&')}`,
        facebook: `${FACEBOOK_SHARE_URL}${params[socialNetwork].join('&')}`,
        twitter: `${TWITTER_SHARE_URL}${params[socialNetwork].join('&')} `,
        linkedIn: `${LINKEDIN_SHARE_URL}${params[socialNetwork].join('&')} `,
      };

      return urls[socialNetwork] || '';
    },

    openSocialWindow(socialNetwork) {
      const width = 600;
      const height = 600;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      const windowName = socialNetwork !== 'email'
        ? `share-modal-${Math.round(Math.random() * 10000)}`
        : '_self';

      const windowFeatures = [
        `left=${left}`,
        `top=${top}`,
        `width=${width}`,
        `height=${height}`,
        `popup=${socialNetwork !== 'email'}`,
      ].join(',');
      window.open(this.getSocialNetworkURL(socialNetwork), windowName, windowFeatures);
    },
  },
};
</script>

<style lang="scss" scoped>
  // TODO: Remove this when dorado is implemented
  @import '~/styles/dorado/main';

  div ::v-deep {
    .modal-dialog-scrollable {
      max-height: calc(100% - 7rem);
    }

    .modal-wishlists .modal-dialog {
      left: 0;
      margin-top: 15rem;
      position: relative;
      top: 0;
    }

    .modal-header {
      padding-bottom: 0;
    }

    .modal-body {
      box-shadow: none;
    }

    .modal-footer {
      display: none !important;
    }

    .share-modal-label {
      color: $dark;
    }
  }

  .share-text {
    line-height: 21px;
    vertical-align: middle;
  }

  .icon-container {
    align-items: center;
    background-color: $light !important;
    cursor: pointer;
    display: flex;
    height: 56px;
    justify-content: center;
    width: 56px;
  }
</style>
